import { useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { Box, GlobalStyles } from "@mui/material";

// Quanto cada seta do teclado muda a largura.
const PASSO_TECLADO = 16;

interface AlcaLarguraColunaProps {
  largura: number;
  min: number;
  max: number;
  rotulo: string;
  onMudar: (largura: number) => void;
  // Duplo clique: ajusta a coluna ao conteúdo, como no Excel.
  onAjustar?: () => void;
}

// Borda arrastável à direita de um cabeçalho de coluna. Vai dentro do <th>, que precisa ser
// posicionado (o sticky da grade já é).
export function AlcaLarguraColuna({ largura, min, max, rotulo, onMudar, onAjustar }: AlcaLarguraColunaProps) {
  const inicio = useRef<{ x: number; largura: number } | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const limitar = (l: number) => Math.round(Math.min(max, Math.max(min, l)));
  const larguraDoArraste = (e: PointerEvent) => limitar(inicio.current!.largura + e.clientX - inicio.current!.x);

  const soltar = (e: PointerEvent) => {
    if (!inicio.current) return;
    const l = larguraDoArraste(e);
    inicio.current = null;
    setArrastando(false);
    onMudar(l);
  };

  const teclas: Record<string, (l: number) => number> = {
    ArrowLeft: (l) => l - PASSO_TECLADO,
    ArrowRight: (l) => l + PASSO_TECLADO,
    Home: () => min,
    End: () => max,
  };

  return (
    <>
      {/* Durante o arraste o cursor não pode trocar ao passar sobre as células, nem o texto ser selecionado. */}
      {arrastando && <GlobalStyles styles={{ "body, body *": { cursor: "col-resize !important", userSelect: "none !important" } }} />}
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label={rotulo}
        aria-valuenow={largura}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={0}
        title="Arraste para ajustar a largura. Duplo clique ajusta ao nome mais longo."
        data-arrastando={arrastando || undefined}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          e.currentTarget.setPointerCapture?.(e.pointerId);
          inicio.current = { x: e.clientX, largura };
          setArrastando(true);
        }}
        onPointerMove={(e) => inicio.current && onMudar(larguraDoArraste(e))}
        onPointerUp={soltar}
        onPointerCancel={soltar}
        onDoubleClick={onAjustar}
        onKeyDown={(e: KeyboardEvent) => {
          const tecla = teclas[e.key];
          if (!tecla) return;
          e.preventDefault();
          onMudar(limitar(tecla(largura)));
        }}
        sx={{
          position: "absolute",
          top: 0,
          // Centrada sobre a borda direita do <th>, mais larga que ela para ser fácil de pegar.
          right: -4,
          width: 9,
          height: "100%",
          zIndex: 1,
          cursor: "col-resize",
          display: "flex",
          justifyContent: "center",
          outline: "none",
          "&::after": { content: '""', width: 2, height: "100%", bgcolor: "transparent", transition: "background-color 120ms" },
          "&:hover::after, &:focus-visible::after, &[data-arrastando]::after": { bgcolor: "primary.main" },
        }}
      />
    </>
  );
}
